/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  NgZone,
  ChangeDetectorRef,
  AfterViewInit,
  inject,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { BackgroundSegmentationService } from '../modules/social/services/video-room/background-segmentation.service';
import { LogService } from 'app/services/logging.service';
import * as faceLandmarksDetection from '@tensorflow-models/face-landmarks-detection';
import '@tensorflow/tfjs-backend-webgl';

// Define a type for facial effect types
export type FacialEffectType =
  | 'dots'
  | 'mesh'
  | 'glasses'
  | 'hat'
  | 'mask'
  | 'cartoon'
  | 'mustache'
  | 'googlyEyes'
  | 'lighting'
  | 'puppet'
  | 'rain' // Weather effect: rain drops
  | 'snow' // Weather effect: snow flakes
  | 'bubbles' // Weather effect: floating bubbles
  | 'sparkles' // Weather effect: sparkle particles
  | 'particles'; // Weather effect: floating particles

@Component({
  selector: 'app-test-segmentation',
  templateUrl: './test-segmentation.component.html',
  styleUrls: ['./test-segmentation.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule],
})
export class TestSegmentationComponent implements OnInit, AfterViewInit, OnDestroy {
  private backgroundSegmentationService = inject(BackgroundSegmentationService);
  private ngZone = inject(NgZone);
  private log = inject(LogService);
  private cdr = inject(ChangeDetectorRef);

  private readonly videoElementRef = viewChild.required<ElementRef<HTMLVideoElement>>('videoEl');
  private readonly maskCanvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('maskCanvas');
  private readonly compositeCanvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('compositeCanvas');
  private readonly pinkBgCanvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('pinkBgCanvas');
  private readonly facialLandmarksCanvasRef =
    viewChild.required<ElementRef<HTMLCanvasElement>>('facialLandmarksCanvas');

  // Existing segmentation parameters
  protected modelLoaded = false;
  protected error: string | null = null;
  protected segmentationThreshold = 0.3; // Default threshold
  protected invertMask = false;

  // New segmentation parameters
  protected flipHorizontal = false;
  protected outputWidth = 256;
  protected outputHeight = 256;
  protected modelType: 'general' | 'landscape' = 'general';
  protected shaderThreshold = 0.1;

  // Facial landmark parameters
  protected facialLandmarksEnabled = true; // Changed from false to true
  protected facialEffectType: FacialEffectType = 'dots';
  protected facialEffectColor = '#00ff00'; // Default green color
  protected facialEffectSize = 3; // Default dot size
  private faceLandmarksModel: faceLandmarksDetection.FaceLandmarksDetector | null = null;
  private faceLandmarksModelLoaded = false;

  // Added properties for mustache image
  private mustacheImage: HTMLImageElement | null = null;
  private mustacheImageLoaded = false;

  // Added properties for googly eyes image
  private googlyEyesImage: HTMLImageElement | null = null;
  private googlyEyesImageLoaded = false;

  private videoStream: MediaStream | null = null;
  private animationFrameId: number | null = null;
  private modelLoadedSubscription: Subscription | null = null;

  public ngOnInit(): void {
    this.modelLoadedSubscription = this.backgroundSegmentationService.modelLoaded.subscribe((loaded) => {
      this.modelLoaded = loaded;
      if (loaded) {
        this.log.info('Segmentation model loaded, starting webcam.');
        void this.startWebcam();

        // Load the facial landmarks detection model
        if (this.facialLandmarksEnabled) {
          void this.loadFaceLandmarksModel();
        }
      } else {
        this.log.warn('Segmentation model not yet loaded.');
      }
      this.cdr.detectChanges();
    });

    // Ensure facial landmarks model loads when enabled
    if (this.facialLandmarksEnabled && !this.faceLandmarksModelLoaded) {
      void this.loadFaceLandmarksModel();
    }
  }

  public ngAfterViewInit(): void {
    // Access to the ViewChild elements is guaranteed here
    if (this.modelLoaded) {
      void this.startWebcam();
    }
  }

  public ngOnDestroy(): void {
    this.stopWebcam();
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    this.modelLoadedSubscription?.unsubscribe();
    this.backgroundSegmentationService.deactivate();
  }

  private async startWebcam(): Promise<void> {
    try {
      this.videoStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 320 },
          height: { ideal: 240 },
          facingMode: 'user',
        },
      });

      const videoEl: HTMLVideoElement = this.videoElementRef().nativeElement;
      videoEl.srcObject = this.videoStream;
      videoEl.onloadedmetadata = async (): Promise<void> => {
        await videoEl.play();
        this.log.info('Webcam stream started.');
        // Activate the segmentation service now that the video is ready
        this.backgroundSegmentationService.activate();
        this.startProcessingLoop();
      };
    } catch (err) {
      this.log.error('Error accessing webcam:', err);
      this.error = 'Could not access webcam. Please ensure permissions are granted.';
      this.cdr.detectChanges();
    }
  }

  private stopWebcam(): void {
    this.videoStream?.getTracks().forEach((track: MediaStreamTrack) => track.stop());
    this.videoStream = null;
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.backgroundSegmentationService.deactivate();

    // Cleanup facial landmarks detector
    if (this.faceLandmarksModel) {
      this.faceLandmarksModel.dispose();
      this.faceLandmarksModel = null;
      this.faceLandmarksModelLoaded = false;
    }

    this.log.info('Webcam stream stopped.');
  }

  private startProcessingLoop(): void {
    const videoEl = this.videoElementRef().nativeElement;
    const maskCanvas = this.maskCanvasRef().nativeElement;
    const compositeCanvas = this.compositeCanvasRef().nativeElement;
    const pinkBgCanvas = this.pinkBgCanvasRef().nativeElement;

    const maskCtx = maskCanvas.getContext('2d', { willReadFrequently: true });
    const compositeCtx = compositeCanvas.getContext('2d', { willReadFrequently: true });
    const pinkBgCtx = pinkBgCanvas.getContext('2d', { willReadFrequently: true });
    const facialLandmarksCtx = this.facialLandmarksCanvasRef()?.nativeElement.getContext('2d', {
      willReadFrequently: true,
    });

    if (!maskCtx || !compositeCtx || !pinkBgCtx) {
      this.log.error('Could not get canvas contexts.');
      this.error = 'Failed to get canvas contexts.';
      this.cdr.detectChanges();
      return;
    }

    const processFrame = async () => {
      if (!this.videoStream || videoEl.paused || videoEl.ended) {
        this.animationFrameId = requestAnimationFrame(processFrame);
        return;
      }

      try {
        // Directly access private segmentationConfig to modify threshold
        // Note: This is a hack for testing purposes only
        // @ts-expect-error - Accessing private property for testing
        this.backgroundSegmentationService.segmentationConfig.segmentationThreshold = this.segmentationThreshold;

        const maskImageData = await this.backgroundSegmentationService.segmentFrame(videoEl);

        // Detect facial landmarks if enabled
        let facialLandmarks = null;
        if (this.facialLandmarksEnabled && this.faceLandmarksModelLoaded && this.faceLandmarksModel) {
          facialLandmarks = await this.faceLandmarksModel.estimateFaces(videoEl);
        }

        this.ngZone.run(() => {
          if (maskImageData) {
            // Ensure canvas sizes match mask size
            if (maskCanvas.width !== maskImageData.width || maskCanvas.height !== maskImageData.height) {
              maskCanvas.width = maskImageData.width;
              maskCanvas.height = maskImageData.height;
              compositeCanvas.width = maskImageData.width;
              compositeCanvas.height = maskImageData.height;
              pinkBgCanvas.width = maskImageData.width;
              pinkBgCanvas.height = maskImageData.height;

              const facialLandmarksCanvasRef = this.facialLandmarksCanvasRef();
              if (facialLandmarksCanvasRef) {
                facialLandmarksCanvasRef.nativeElement.width = maskImageData.width;
                facialLandmarksCanvasRef.nativeElement.height = maskImageData.height;
              }
            }

            // Option to invert the mask for testing
            if (this.invertMask) {
              this.invertMaskData(maskImageData);
            }

            // Draw the mask
            maskCtx.putImageData(maskImageData, 0, 0);

            // Create composite view (original video with mask overlay)
            compositeCtx.drawImage(videoEl, 0, 0, compositeCanvas.width, compositeCanvas.height);

            // Add semi-transparent red overlay where mask is
            compositeCtx.save();
            compositeCtx.globalCompositeOperation = 'source-over';
            compositeCtx.globalAlpha = 0.5;
            compositeCtx.fillStyle = 'rgba(255, 0, 0, 0.5)';

            // Create a path from the mask
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = maskImageData.width;
            tempCanvas.height = maskImageData.height;
            const tempCtx = tempCanvas.getContext('2d');

            if (tempCtx) {
              // Draw the mask to the temp canvas
              tempCtx.putImageData(maskImageData, 0, 0);

              // Use the temp canvas as source for composite operation
              compositeCtx.drawImage(tempCanvas, 0, 0);

              // Create the pink background preview - FIXED VERSION
              // Clear the canvas
              pinkBgCtx.clearRect(0, 0, pinkBgCanvas.width, pinkBgCanvas.height);

              // Fill with pink background
              pinkBgCtx.fillStyle = '#ff69b4'; // Hot pink
              pinkBgCtx.fillRect(0, 0, pinkBgCanvas.width, pinkBgCanvas.height);

              // Create a temporary canvas for the video frame
              const videoCanvas = document.createElement('canvas');
              videoCanvas.width = maskImageData.width;
              videoCanvas.height = maskImageData.height;
              const videoCtx = videoCanvas.getContext('2d');

              if (videoCtx) {
                // Draw the video frame
                videoCtx.drawImage(videoEl, 0, 0, videoCanvas.width, videoCanvas.height);

                // Set up the binary mask approach
                const binaryMask = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
                const resultData = this.processPixelData(
                  binaryMask,
                  videoCtx.getImageData(0, 0, videoCanvas.width, videoCanvas.height),
                );

                // Put the composite image onto the canvas
                pinkBgCtx.putImageData(resultData, 0, 0);

                // Clean up
                videoCanvas.remove();
              }
            }

            compositeCtx.restore();

            // Render facial landmarks if available
            if (facialLandmarksCtx && facialLandmarks && facialLandmarks.length > 0) {
              // Clear the canvas
              facialLandmarksCtx.clearRect(0, 0, facialLandmarksCtx.canvas.width, facialLandmarksCtx.canvas.height);

              // Render based on the selected effect type
              this.renderFacialLandmarks(facialLandmarksCtx, facialLandmarks);
            } else if (facialLandmarksCtx) {
              // Clear the canvas if no landmarks detected
              facialLandmarksCtx.clearRect(0, 0, facialLandmarksCtx.canvas.width, facialLandmarksCtx.canvas.height);

              if (this.facialLandmarksEnabled) {
                // Display a message if facial landmarks are enabled but none detected
                facialLandmarksCtx.fillStyle = 'white';
                facialLandmarksCtx.font = '16px Arial';
                facialLandmarksCtx.fillText('No face detected', 10, 30);
              }
            }
          } else {
            maskCtx.clearRect(0, 0, maskCanvas.width, maskCanvas.height);
            compositeCtx.clearRect(0, 0, compositeCanvas.width, compositeCanvas.height);
            pinkBgCtx.clearRect(0, 0, pinkBgCanvas.width, pinkBgCanvas.height);

            if (facialLandmarksCtx) {
              facialLandmarksCtx.clearRect(0, 0, facialLandmarksCtx.canvas.width, facialLandmarksCtx.canvas.height);
            }

            compositeCtx.fillStyle = 'black';
            compositeCtx.font = '16px Arial';
            compositeCtx.fillText('No mask data available', 10, 30);

            pinkBgCtx.fillStyle = 'black';
            pinkBgCtx.font = '16px Arial';
            pinkBgCtx.fillText('No mask data available', 10, 30);
          }
        });
      } catch (err) {
        this.log.error('Error during segmentation frame processing:', err);
      }

      // Continue the loop
      this.animationFrameId = requestAnimationFrame(processFrame);
    };

    // Run the loop outside Angular zone
    this.ngZone.runOutsideAngular(() => {
      this.animationFrameId = requestAnimationFrame(processFrame);
    });
  }

  // Invert the mask data for testing purposes
  private invertMaskData(imageData: ImageData): void {
    for (let i = 0; i < imageData.data.length; i += 4) {
      imageData.data[i] = 255 - imageData.data[i]; // R
      imageData.data[i + 1] = 255 - imageData.data[i + 1]; // G
      imageData.data[i + 2] = 255 - imageData.data[i + 2]; // B
      // Don't modify alpha
    }
  }

  // UI Controls for existing parameters
  public toggleMaskInversion(): void {
    this.invertMask = !this.invertMask;
  }

  public adjustThreshold(delta: number): void {
    this.segmentationThreshold = Math.max(0.01, Math.min(0.99, this.segmentationThreshold + delta));
    // The change will be applied in the next frame
  }

  // UI Controls for new parameters
  public toggleFlipHorizontal(): void {
    this.flipHorizontal = !this.flipHorizontal;

    // Update the service configuration
    // @ts-expect-error - Accessing private property for testing
    this.backgroundSegmentationService.segmentationConfig.flipHorizontal = this.flipHorizontal;

    this.log.info(`Flip horizontal set to: ${this.flipHorizontal}`);
  }

  public changeResolution(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    const [width, height]: [number, number] = (selectElement.value.split('x').map(Number) as [number, number]) || [
      0, 0,
    ];

    this.outputWidth = width;
    this.outputHeight = height;

    // Update the service output resolution
    // @ts-expect-error - Accessing private property for testing
    this.backgroundSegmentationService.outputWidth = this.outputWidth;
    // @ts-expect-error - Accessing private property for testing
    this.backgroundSegmentationService.outputHeight = this.outputHeight;

    this.log.info(`Resolution changed to: ${this.outputWidth}x${this.outputHeight}`);

    // Reset the canvas to reflect new dimensions
    this.resetCanvasDimensions();
  }

  public changeModelType(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    this.modelType = selectElement.value as 'general' | 'landscape';

    this.log.info(`Model type selected: ${this.modelType}`);
    // Note: Model type change requires a model reload to take effect
  }

  public reloadModel(): void {
    this.log.info('Reloading segmentation model...');
    this.stopWebcam();
    this.modelLoaded = false;
    this.error = null;

    // Deactivate the current service
    this.backgroundSegmentationService.deactivate();

    // We need to recreate the service to change the model type
    // This is a workaround since the model type is set during creation
    this.backgroundSegmentationService.destroy();

    // Wait a moment before reloading
    setTimeout(() => {
      // @ts-expect-error - Setting private property for testing
      this.backgroundSegmentationService.modelType = this.modelType;

      // Trigger a reload of the model
      this.backgroundSegmentationService
        // @ts-expect-error - Accessing private method for testing
        .loadModel()
        .then(() => {
          this.log.info('Model reloaded successfully.');
          void this.startWebcam();
        })
        .catch((error) => {
          this.log.error('Failed to reload model:', error);
          this.error = 'Failed to reload segmentation model.';
          this.cdr.detectChanges();
        });
    }, 500);
  }

  public adjustShaderThreshold(delta: number): void {
    this.shaderThreshold = Math.max(0.01, Math.min(0.99, this.shaderThreshold + delta));

    // To actually update the shader threshold, we need to modify the pixel data processing in our test component
    // since we don't have direct access to the shader uniform in the service through the test component

    // For the pink background preview, we can update the threshold value used in our pixel comparison
    // This will have the same visual effect as adjusting the shader threshold
    this.log.info(`Shader threshold adjusted to: ${this.shaderThreshold}`);
  }

  // Modified process frame function to use shader threshold for pink background preview
  private processPixelData(binaryMask: ImageData, rawVideoData: ImageData): ImageData {
    const resultData = new ImageData(
      new Uint8ClampedArray(binaryMask.data.length),
      binaryMask.width,
      binaryMask.height,
    );

    // First copy the pink background to our result data
    for (let i = 0; i < resultData.data.length; i += 4) {
      resultData.data[i] = 255; // R (pink)
      resultData.data[i + 1] = 105; // G (pink)
      resultData.data[i + 2] = 180; // B (pink)
      resultData.data[i + 3] = 255; // A (fully opaque)
    }

    // Then overlay the person pixels from the video using our shader threshold
    for (let i = 0; i < binaryMask.data.length; i += 4) {
      // Instead of using a hardcoded value (127), use our configurable shader threshold
      // Convert shader threshold (0-1) to pixel value range (0-255)
      const pixelThreshold = this.shaderThreshold * 255;

      // If mask pixel is above our threshold, copy the video pixel
      const isMaskPixelAboveThreshold = binaryMask.data[i] > pixelThreshold;

      // Only show video pixels where the mask is above threshold (the person)
      if (isMaskPixelAboveThreshold !== this.invertMask) {
        resultData.data[i] = rawVideoData.data[i]; // R
        resultData.data[i + 1] = rawVideoData.data[i + 1]; // G
        resultData.data[i + 2] = rawVideoData.data[i + 2]; // B
        resultData.data[i + 3] = 255; // A (fully opaque)
      }
    }

    return resultData;
  }

  private resetCanvasDimensions(): void {
    // Reset the display canvases to match the new output dimensions
    const maskCanvasRef = this.maskCanvasRef();
    const compositeCanvasRef = this.compositeCanvasRef();
    const pinkBgCanvasRef = this.pinkBgCanvasRef();
    if (maskCanvasRef && compositeCanvasRef && pinkBgCanvasRef) {
      const maskCanvas = maskCanvasRef.nativeElement;
      const compositeCanvas = compositeCanvasRef.nativeElement;
      const pinkBgCanvas = pinkBgCanvasRef.nativeElement;

      maskCanvas.width = this.outputWidth;
      maskCanvas.height = this.outputHeight;
      compositeCanvas.width = this.outputWidth;
      compositeCanvas.height = this.outputHeight;
      pinkBgCanvas.width = this.outputWidth;
      pinkBgCanvas.height = this.outputHeight;
    }
  }

  // Facial landmarks methods
  private async loadFaceLandmarksModel(): Promise<void> {
    try {
      this.log.info('Loading facial landmarks detection model...');

      // Load the MediaPipe FaceMesh model
      this.faceLandmarksModel = await faceLandmarksDetection.createDetector(
        faceLandmarksDetection.SupportedModels.MediaPipeFaceMesh,
        {
          runtime: 'tfjs',
          refineLandmarks: true,
          maxFaces: 1, // For testing, just detect one face
        },
      );

      this.faceLandmarksModelLoaded = true;
      this.log.info('Facial landmarks detection model loaded successfully');
      this.cdr.detectChanges();
    } catch (err) {
      this.log.error('Error loading facial landmarks detection model:', err);
      this.error = 'Failed to load facial landmarks detection model.';
      this.faceLandmarksModelLoaded = false;
      this.cdr.detectChanges();
    }
  }

  // Toggle facial landmarks detection on/off
  public toggleFacialLandmarks(): void {
    this.facialLandmarksEnabled = !this.facialLandmarksEnabled;

    if (this.facialLandmarksEnabled && !this.faceLandmarksModelLoaded) {
      void this.loadFaceLandmarksModel();
    }

    this.log.info(`Facial landmarks detection ${this.facialLandmarksEnabled ? 'enabled' : 'disabled'}`);
  }

  // Change the facial effect type
  public changeFacialEffectType(type: FacialEffectType): void {
    this.facialEffectType = type;
    this.log.info(`Facial effect type changed to: ${type}`);

    if (type === 'mustache' && !this.mustacheImageLoaded) {
      this.loadMustacheImage();
    }

    // Load googly eyes image if the effect type is selected
    if (this.facialEffectType === 'googlyEyes' && !this.googlyEyesImageLoaded) {
      this.loadGooglyEyesImage();
    }
  }

  // Change the facial effect color
  public changeFacialEffectColor(color: string): void {
    this.facialEffectColor = color;
    this.log.info(`Facial effect color changed to: ${color}`);
  }

  // Adjust the size of facial effect elements
  public adjustFacialEffectSize(delta: number): void {
    this.facialEffectSize = Math.max(1, Math.min(10, this.facialEffectSize + delta));
    this.log.info(`Facial effect size adjusted to: ${this.facialEffectSize}`);
  }

  // Reload the facial landmarks model
  public reloadFacialLandmarksModel(): void {
    this.log.info('Reloading facial landmarks detection model...');

    // Clean up existing model if it exists
    if (this.faceLandmarksModel) {
      this.faceLandmarksModel.dispose();
      this.faceLandmarksModel = null;
      this.faceLandmarksModelLoaded = false;
    }

    // Load the model again
    void this.loadFaceLandmarksModel();
  }

  // Render facial landmarks based on selected effect type
  private renderFacialLandmarks(ctx: CanvasRenderingContext2D, faces: faceLandmarksDetection.Face[]): void {
    // Clear the canvas first
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

    // Draw the unaltered video feed as background first
    const videoEl = this.videoElementRef().nativeElement;
    ctx.drawImage(videoEl, 0, 0, ctx.canvas.width, ctx.canvas.height);

    faces.forEach((face) => {
      const keypoints = face.keypoints;

      if (!keypoints || keypoints.length === 0) {
        return;
      }

      // Set the color based on user selection
      ctx.fillStyle = this.facialEffectColor;
      ctx.strokeStyle = this.facialEffectColor;

      if (this.facialEffectType === 'mustache' && !this.mustacheImageLoaded) {
        this.loadMustacheImage();
      }

      switch (this.facialEffectType) {
        case 'dots':
          this.renderDots(ctx, keypoints);
          break;
        case 'mesh':
          this.renderMesh(ctx, keypoints);
          break;
        case 'glasses':
          this.renderGlasses(ctx, keypoints);
          break;
        case 'hat':
          this.renderHat(ctx, keypoints);
          break;
        case 'mask':
          this.renderMask(ctx, keypoints);
          break;
        case 'cartoon':
          this.renderCartoon(ctx, keypoints);
          break;
        case 'mustache':
          this.renderMustache(ctx, keypoints);
          break;
        case 'googlyEyes':
          this.renderGooglyEyes(ctx, keypoints);
          break;
        case 'lighting':
          this.renderLighting(ctx, keypoints);
          break;
        case 'puppet':
          this.renderPuppet(ctx, keypoints);
          break;
        // Add the new weather effects
        case 'rain':
          this.renderRain(ctx, keypoints);
          break;
        case 'snow':
          this.renderSnow(ctx, keypoints);
          break;
        case 'bubbles':
          this.renderBubbles(ctx, keypoints);
          break;
        case 'sparkles':
          this.renderSparkles(ctx, keypoints);
          break;
        case 'particles':
          this.renderParticles(ctx, keypoints);
          break;
        default:
          this.renderDots(ctx, keypoints);
      }
    });
  }

  // Render simple dots for each facial landmark
  private renderDots(ctx: CanvasRenderingContext2D, keypoints: readonly faceLandmarksDetection.Keypoint[]): void {
    ctx.beginPath();
    for (const point of keypoints) {
      const x: number = point.x;
      const y: number = point.y;

      ctx.moveTo(x, y);
      ctx.arc(x, y, this.facialEffectSize, 0, 2 * Math.PI);
    }
    ctx.fill();
  }

  // Render a mesh by connecting facial landmarks
  private renderMesh(ctx: CanvasRenderingContext2D, keypoints: readonly faceLandmarksDetection.Keypoint[]): void {
    // Draw lines between key points to create a mesh effect
    ctx.lineWidth = this.facialEffectSize / 2;

    // Draw face contour
    const faceOval: readonly faceLandmarksDetection.Keypoint[] = keypoints.slice(0, 36);
    ctx.beginPath();
    if (faceOval.length > 0) {
      ctx.moveTo(faceOval[0].x, faceOval[0].y);
      for (let i = 1; i < faceOval.length; i++) {
        ctx.lineTo(faceOval[i].x, faceOval[i].y);
      }
      ctx.closePath();
      ctx.stroke();
    }

    // Draw eyes
    const leftEye = [
      keypoints[33],
      keypoints[246],
      keypoints[161],
      keypoints[160],
      keypoints[159],
      keypoints[158],
      keypoints[157],
      keypoints[173],
    ];
    const rightEye = [
      keypoints[398],
      keypoints[384],
      keypoints[385],
      keypoints[386],
      keypoints[387],
      keypoints[388],
      keypoints[466],
      keypoints[263],
    ];

    this.drawConnectedPoints(ctx, leftEye);
    this.drawConnectedPoints(ctx, rightEye);

    // Draw eyebrows
    const leftEyebrow = [keypoints[70], keypoints[63], keypoints[105], keypoints[66], keypoints[107]];
    const rightEyebrow = [keypoints[336], keypoints[296], keypoints[334], keypoints[293], keypoints[300]];

    this.drawConnectedPoints(ctx, leftEyebrow);
    this.drawConnectedPoints(ctx, rightEyebrow);

    // Draw nose
    const noseBridge = [keypoints[168], keypoints[6], keypoints[197], keypoints[195], keypoints[5]];
    this.drawConnectedPoints(ctx, noseBridge);

    // Draw mouth
    const outerLips = [
      keypoints[61],
      keypoints[185],
      keypoints[40],
      keypoints[39],
      keypoints[37],
      keypoints[0],
      keypoints[267],
      keypoints[269],
      keypoints[270],
      keypoints[409],
      keypoints[291],
      keypoints[375],
    ];

    this.drawConnectedPoints(ctx, outerLips);
  }

  // Helper function to draw connected points
  private drawConnectedPoints(ctx: CanvasRenderingContext2D, points: readonly faceLandmarksDetection.Keypoint[]): void {
    if (points.length < 2) {
      return;
    }

    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);

    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y);
    }

    ctx.closePath();
    ctx.stroke();
  }

  // Render fun glasses effect
  private renderGlasses(ctx: CanvasRenderingContext2D, keypoints: readonly faceLandmarksDetection.Keypoint[]): void {
    // Find eye landmarks
    const leftEyeCenter = keypoints[468]; // Left eye center
    const rightEyeCenter = keypoints[473]; // Right eye center

    if (!leftEyeCenter || !rightEyeCenter) {
      return;
    }

    const eyeDistance = Math.hypot(rightEyeCenter.x - leftEyeCenter.x, rightEyeCenter.y - leftEyeCenter.y);

    const glassesWidth = eyeDistance * 1.5;
    const lensRadius = eyeDistance / 3.5;
    const bridgeWidth = eyeDistance / 4;

    ctx.lineWidth = this.facialEffectSize;

    // Draw left lens
    ctx.beginPath();
    ctx.arc(leftEyeCenter.x, leftEyeCenter.y, lensRadius, 0, 2 * Math.PI);
    ctx.stroke();

    // Draw right lens
    ctx.beginPath();
    ctx.arc(rightEyeCenter.x, rightEyeCenter.y, lensRadius, 0, 2 * Math.PI);
    ctx.stroke();

    // Draw bridge
    ctx.beginPath();
    ctx.moveTo(leftEyeCenter.x + lensRadius * 0.6, leftEyeCenter.y - lensRadius * 0.3);
    ctx.lineTo(rightEyeCenter.x - lensRadius * 0.6, rightEyeCenter.y - lensRadius * 0.3);
    ctx.stroke();

    // Draw temple arms
    ctx.beginPath();
    ctx.moveTo(leftEyeCenter.x - lensRadius, leftEyeCenter.y);
    ctx.lineTo(leftEyeCenter.x - lensRadius * 2, leftEyeCenter.y - lensRadius * 0.5);
    ctx.moveTo(rightEyeCenter.x + lensRadius, rightEyeCenter.y);
    ctx.lineTo(rightEyeCenter.x + lensRadius * 2, rightEyeCenter.y - lensRadius * 0.5);
    ctx.stroke();
  }

  // Render hat effect
  private renderHat(ctx: CanvasRenderingContext2D, keypoints: readonly faceLandmarksDetection.Keypoint[]): void {
    // Use forehead points to position the hat
    const foreheadTop = keypoints[10]; // Top of forehead
    const leftTemple = keypoints[234]; // Left temple
    const rightTemple = keypoints[454]; // Right temple

    if (!foreheadTop || !leftTemple || !rightTemple) {
      return;
    }

    const faceWidth = Math.hypot(rightTemple.x - leftTemple.x, rightTemple.y - leftTemple.y);

    const hatWidth = faceWidth * 1.4;
    const hatHeight = hatWidth * 0.6;
    const brimWidth = hatWidth * 1.2;
    const brimHeight = hatHeight * 0.2;

    ctx.lineWidth = this.facialEffectSize;

    // Draw hat brim
    ctx.beginPath();
    ctx.ellipse(foreheadTop.x, foreheadTop.y - hatHeight * 0.4, brimWidth / 2, brimHeight, 0, 0, 2 * Math.PI);
    ctx.stroke();

    // Draw hat crown
    ctx.beginPath();
    ctx.ellipse(foreheadTop.x, foreheadTop.y - hatHeight * 0.8, hatWidth / 2, hatHeight / 2, 0, Math.PI, 2 * Math.PI);
    ctx.stroke();

    // Connect hat crown to brim with lines
    ctx.beginPath();
    ctx.moveTo(foreheadTop.x - hatWidth / 2, foreheadTop.y - hatHeight * 0.8);
    ctx.lineTo(foreheadTop.x - brimWidth / 2, foreheadTop.y - hatHeight * 0.4);
    ctx.moveTo(foreheadTop.x + hatWidth / 2, foreheadTop.y - hatHeight * 0.8);
    ctx.lineTo(foreheadTop.x + brimWidth / 2, foreheadTop.y - hatHeight * 0.4);
    ctx.stroke();
  }

  // Render face mask effect
  private renderMask(ctx: CanvasRenderingContext2D, keypoints: readonly faceLandmarksDetection.Keypoint[]): void {
    // Points for mask positioning
    const nose = keypoints[1]; // Nose tip
    const leftCheek = keypoints[234];
    const rightCheek = keypoints[454];
    const chinBottom = keypoints[152];

    if (!nose || !leftCheek || !rightCheek || !chinBottom) {
      return;
    }

    const faceWidth = Math.hypot(rightCheek.x - leftCheek.x, rightCheek.y - leftCheek.y);

    const maskWidth = faceWidth * 1.2;
    const maskHeight = maskWidth * 0.7;

    ctx.lineWidth = this.facialEffectSize;

    // Draw mask outline
    ctx.beginPath();
    ctx.moveTo(leftCheek.x, leftCheek.y);
    ctx.quadraticCurveTo(nose.x, nose.y + maskHeight * 0.1, rightCheek.x, rightCheek.y);
    ctx.quadraticCurveTo(rightCheek.x, rightCheek.y + maskHeight * 0.5, nose.x + maskWidth * 0.1, chinBottom.y);
    ctx.quadraticCurveTo(nose.x, chinBottom.y + maskHeight * 0.1, nose.x - maskWidth * 0.1, chinBottom.y);
    ctx.quadraticCurveTo(leftCheek.x, leftCheek.y + maskHeight * 0.5, leftCheek.x, leftCheek.y);
    ctx.stroke();

    // Draw mask folds/pleats
    const centerX = nose.x;
    const centerY = (nose.y + chinBottom.y) / 2;

    ctx.beginPath();
    for (let i = -2; i <= 2; i++) {
      const lineX = centerX + i * maskWidth * 0.1;
      const lineHeight = maskHeight * 0.4;

      ctx.moveTo(lineX, centerY - lineHeight / 2);
      ctx.lineTo(lineX, centerY + lineHeight / 2);
    }
    ctx.stroke();

    // Draw ear loops
    ctx.beginPath();
    ctx.moveTo(leftCheek.x, leftCheek.y);
    ctx.quadraticCurveTo(
      leftCheek.x - maskWidth * 0.15,
      leftCheek.y,
      leftCheek.x - maskWidth * 0.2,
      leftCheek.y - maskHeight * 0.2,
    );

    ctx.moveTo(rightCheek.x, rightCheek.y);
    ctx.quadraticCurveTo(
      rightCheek.x + maskWidth * 0.15,
      rightCheek.y,
      rightCheek.x + maskWidth * 0.2,
      rightCheek.y - maskHeight * 0.2,
    );
    ctx.stroke();
  }

  // Render cartoon filter effect
  private renderCartoon(ctx: CanvasRenderingContext2D, keypoints: readonly faceLandmarksDetection.Keypoint[]): void {
    // Get key facial features for cartoon effect
    const faceOval = keypoints.slice(0, 36); // Face outline
    const leftEye = [
      keypoints[33],
      keypoints[246],
      keypoints[161],
      keypoints[160],
      keypoints[159],
      keypoints[158],
      keypoints[157],
      keypoints[173],
    ];
    const rightEye = [
      keypoints[398],
      keypoints[384],
      keypoints[385],
      keypoints[386],
      keypoints[387],
      keypoints[388],
      keypoints[466],
      keypoints[263],
    ];
    const leftEyebrow = [keypoints[70], keypoints[63], keypoints[105], keypoints[66], keypoints[107]];
    const rightEyebrow = [keypoints[336], keypoints[296], keypoints[334], keypoints[293], keypoints[300]];
    const nose = [keypoints[168], keypoints[6], keypoints[197], keypoints[195], keypoints[5]];
    const mouth = [
      keypoints[61],
      keypoints[185],
      keypoints[40],
      keypoints[39],
      keypoints[37],
      keypoints[0],
      keypoints[267],
      keypoints[269],
      keypoints[270],
      keypoints[409],
      keypoints[291],
      keypoints[375],
    ];

    // Set line properties for cartoon style
    ctx.lineWidth = this.facialEffectSize * 2; // Thicker lines for cartoon style
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Draw exaggerated face outline
    if (faceOval.length > 0) {
      ctx.beginPath();
      ctx.moveTo(faceOval[0].x, faceOval[0].y);
      for (let i = 1; i < faceOval.length; i += 3) {
        // Skip points for smoother cartoon outline
        ctx.lineTo(faceOval[i].x, faceOval[i].y);
      }
      ctx.closePath();
      ctx.stroke();
    }

    // Draw simplified eyes (larger and more cartoon-like)
    const leftEyeCenter = keypoints[468]; // Left eye center
    const rightEyeCenter = keypoints[473]; // Right eye center

    if (leftEyeCenter && rightEyeCenter) {
      const eyeDistance = Math.hypot(rightEyeCenter.x - leftEyeCenter.x, rightEyeCenter.y - leftEyeCenter.y);

      const eyeSize = ((eyeDistance / 5) * this.facialEffectSize) / 2;

      // Draw cartoon eyes
      ctx.beginPath();
      ctx.ellipse(leftEyeCenter.x, leftEyeCenter.y, eyeSize * 1.2, eyeSize, 0, 0, 2 * Math.PI);
      ctx.ellipse(rightEyeCenter.x, rightEyeCenter.y, eyeSize * 1.2, eyeSize, 0, 0, 2 * Math.PI);
      ctx.stroke();

      // Draw pupils
      ctx.fillStyle = this.facialEffectColor;
      ctx.beginPath();
      ctx.arc(leftEyeCenter.x, leftEyeCenter.y, eyeSize * 0.5, 0, 2 * Math.PI);
      ctx.arc(rightEyeCenter.x, rightEyeCenter.y, eyeSize * 0.5, 0, 2 * Math.PI);
      ctx.fill();
    }

    // Draw exaggerated eyebrows
    this.drawCartoonEyebrows(ctx, leftEyebrow, rightEyebrow);

    // Draw simplified nose
    if (nose.length > 0) {
      const noseTop = nose[0];
      const noseTip = nose[2];
      const noseWidth = Math.abs(nose[4].x - nose[3].x) * 1.2;

      ctx.beginPath();
      ctx.moveTo(noseTop.x, noseTop.y);
      ctx.lineTo(noseTip.x, noseTip.y);
      ctx.lineTo(noseTip.x + noseWidth / 2, noseTip.y);
      ctx.stroke();
    }

    // Draw simplified mouth
    if (mouth.length > 0) {
      const mouthLeft = mouth[0];
      const mouthRight = mouth[6];
      const mouthTop = mouth[3];
      const mouthBottom = mouth[9];

      // Draw simple cartoon mouth
      ctx.beginPath();
      ctx.moveTo(mouthLeft.x, mouthLeft.y);
      ctx.quadraticCurveTo(
        (mouthLeft.x + mouthRight.x) / 2,
        mouthBottom.y + this.facialEffectSize * 2,
        mouthRight.x,
        mouthRight.y,
      );
      ctx.stroke();
    }
  }

  // Helper function for drawing cartoon eyebrows
  private drawCartoonEyebrows(
    ctx: CanvasRenderingContext2D,
    leftEyebrow: readonly faceLandmarksDetection.Keypoint[],
    rightEyebrow: readonly faceLandmarksDetection.Keypoint[],
  ): void {
    if (leftEyebrow.length < 2 || rightEyebrow.length < 2) {
      return;
    }

    // Exaggerate eyebrow positions for cartoon effect
    const leftEyebrowStart = leftEyebrow[0];
    const leftEyebrowEnd = leftEyebrow[leftEyebrow.length - 1];
    const leftEyebrowMid = leftEyebrow[Math.floor(leftEyebrow.length / 2)];

    const rightEyebrowStart = rightEyebrow[0];
    const rightEyebrowEnd = rightEyebrow[rightEyebrow.length - 1];
    const rightEyebrowMid = rightEyebrow[Math.floor(rightEyebrow.length / 2)];

    // Left eyebrow (exaggerated curve)
    ctx.beginPath();
    ctx.moveTo(leftEyebrowStart.x, leftEyebrowStart.y);
    ctx.quadraticCurveTo(
      leftEyebrowMid.x,
      leftEyebrowMid.y - this.facialEffectSize * 3, // Exaggerate the curve upward
      leftEyebrowEnd.x,
      leftEyebrowEnd.y,
    );
    ctx.stroke();

    // Right eyebrow (exaggerated curve)
    ctx.beginPath();
    ctx.moveTo(rightEyebrowStart.x, rightEyebrowStart.y);
    ctx.quadraticCurveTo(
      rightEyebrowMid.x,
      rightEyebrowMid.y - this.facialEffectSize * 3, // Exaggerate the curve upward
      rightEyebrowEnd.x,
      rightEyebrowEnd.y,
    );
    ctx.stroke();
  }

  // Added renderMustache method
  private renderMustache(ctx: CanvasRenderingContext2D, keypoints: readonly faceLandmarksDetection.Keypoint[]): void {
    if (!this.mustacheImage || !this.mustacheImageLoaded) {
      ctx.fillStyle = 'red';
      ctx.font = '12px Arial';
      ctx.fillText('Mustache loading...', 10, ctx.canvas.height - 10);
      return;
    }

    const noseBottom = keypoints[2];
    const upperLipTop = keypoints[0];
    const leftMouthCorner = keypoints[61];
    const rightMouthCorner = keypoints[291];

    if (!noseBottom || !upperLipTop || !leftMouthCorner || !rightMouthCorner) {
      return;
    }

    const mouthWidth = Math.hypot(rightMouthCorner.x - leftMouthCorner.x, rightMouthCorner.y - leftMouthCorner.y);

    const scaleFactor = 3;
    const mustacheTargetWidth = mouthWidth * scaleFactor;
    const scale = mustacheTargetWidth / this.mustacheImage.naturalWidth;
    const mustacheTargetHeight = this.mustacheImage.naturalHeight * scale;

    const centerX = noseBottom.x;
    const centerY = noseBottom.y + (upperLipTop.y - noseBottom.y) * 0.5;

    const angle = Math.atan2(rightMouthCorner.y - leftMouthCorner.y, rightMouthCorner.x - leftMouthCorner.x);

    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.rotate(angle);
    ctx.drawImage(
      this.mustacheImage,
      -mustacheTargetWidth / 2,
      -mustacheTargetHeight / 2,
      mustacheTargetWidth,
      mustacheTargetHeight,
    );
    ctx.restore();
  }

  // Method to load the mustache image asset
  private loadMustacheImage(): void {
    if (this.mustacheImageLoaded || !this.facialLandmarksEnabled) {
      return;
    }

    this.log.info('Loading mustache image...');
    this.mustacheImage = new Image();
    this.mustacheImage.onload = () => {
      this.mustacheImageLoaded = true;
      this.log.info('Mustache image loaded successfully.');
      this.cdr.detectChanges();
    };
    this.mustacheImage.onerror = (err) => {
      this.log.error('Failed to load mustache image:', err);
      this.mustacheImage = null;
    };
    this.mustacheImage.src = 'assets/mustache.png';
  }

  // Added renderGooglyEyes method
  private renderGooglyEyes(ctx: CanvasRenderingContext2D, keypoints: readonly faceLandmarksDetection.Keypoint[]): void {
    if (!this.mustacheImage || !this.mustacheImageLoaded) {
      ctx.fillStyle = 'red';
      ctx.font = '12px Arial';
      ctx.fillText('Googly eyes loading...', 10, ctx.canvas.height - 10);
      return;
    }

    const leftEyeCenter = keypoints[468]; // Left eye center
    const rightEyeCenter = keypoints[473]; // Right eye center

    if (!leftEyeCenter || !rightEyeCenter) {
      return;
    }

    const eyeDistance = Math.hypot(rightEyeCenter.x - leftEyeCenter.x, rightEyeCenter.y - leftEyeCenter.y);
    const eyeSize = eyeDistance / 3;

    // Draw googly eyes for each eye
    [leftEyeCenter, rightEyeCenter].forEach((eyeCenter) => {
      const pupilOffsetX = (Math.random() - 0.5) * eyeSize * 0.2;
      const pupilOffsetY = (Math.random() - 0.5) * eyeSize * 0.2;

      ctx.save();
      ctx.translate(eyeCenter.x, eyeCenter.y);

      // Draw the white part of the eye
      ctx.fillStyle = 'white';
      ctx.beginPath();
      ctx.arc(0, 0, eyeSize, 0, 2 * Math.PI);
      ctx.fill();

      // Draw the pupil
      ctx.fillStyle = 'black';
      ctx.beginPath();
      ctx.arc(pupilOffsetX, pupilOffsetY, eyeSize * 0.3, 0, 2 * Math.PI);
      ctx.fill();

      ctx.restore();
    });
  }

  // Method to load the googly eyes image asset
  private loadGooglyEyesImage(): void {
    if (this.googlyEyesImageLoaded || !this.facialLandmarksEnabled) {
      return;
    }

    this.log.info('Loading googly eyes image...');
    this.googlyEyesImage = new Image();
    this.googlyEyesImage.onload = () => {
      this.googlyEyesImageLoaded = true;
      this.log.info('Googly eyes image loaded successfully.');
      this.cdr.detectChanges();
    };
    this.googlyEyesImage.onerror = (err) => {
      this.log.error('Failed to load googly eyes image:', err);
      this.googlyEyesImage = null;
    };
    this.googlyEyesImage.src = 'assets/eyes.png';
  }

  // Render face-aware lighting effect
  private renderLighting(ctx: CanvasRenderingContext2D, keypoints: readonly faceLandmarksDetection.Keypoint[]): void {
    // Get important face points for light direction
    const noseTop = keypoints[168]; // Top of nose
    const noseTip = keypoints[1]; // Tip of nose
    const leftCheek = keypoints[234]; // Left cheek
    const rightCheek = keypoints[454]; // Right cheek
    const forehead = keypoints[10]; // Forehead
    const chin = keypoints[152]; // Chin

    if (!noseTop || !noseTip || !leftCheek || !rightCheek || !forehead || !chin) {
      return;
    }

    // Calculate face center
    const faceCenter = {
      x: (leftCheek.x + rightCheek.x) / 2,
      y: (forehead.y + chin.y) / 2,
    };

    // Calculate face dimensions
    const faceWidth = Math.hypot(rightCheek.x - leftCheek.x, rightCheek.y - leftCheek.y);
    const faceHeight = Math.hypot(forehead.y - chin.y, forehead.x - chin.x);

    // Get head pose/orientation based on nose direction
    const noseVector = {
      x: noseTip.x - noseTop.x,
      y: noseTip.y - noseTop.y,
    };

    // Normalize nose vector
    const noseVectorLength = Math.sqrt(noseVector.x * noseVector.x + noseVector.y * noseVector.y);
    const noseDirection = {
      x: noseVector.x / noseVectorLength,
      y: noseVector.y / noseVectorLength,
    };

    // Create a gradient that follows the head orientation
    // Light source is opposite to where the nose is pointing
    const lightDirection = {
      x: -noseDirection.x,
      y: -noseDirection.y,
    };

    // Determine gradient start and end points
    const gradientStartX = faceCenter.x - lightDirection.x * faceWidth;
    const gradientStartY = faceCenter.y - lightDirection.y * faceHeight;
    const gradientEndX = faceCenter.x + lightDirection.x * faceWidth;
    const gradientEndY = faceCenter.y + lightDirection.y * faceHeight;

    // Create radial gradient for spotlight effect
    const spotlightRadius = Math.max(faceWidth, faceHeight) * 1.2;
    const radialGradient = ctx.createRadialGradient(
      faceCenter.x,
      faceCenter.y,
      0,
      faceCenter.x,
      faceCenter.y,
      spotlightRadius,
    );

    // Parse the color from facialEffectColor (which is in hex format)
    const r = parseInt(this.facialEffectColor.slice(1, 3), 16);
    const g = parseInt(this.facialEffectColor.slice(3, 5), 16);
    const b = parseInt(this.facialEffectColor.slice(5, 7), 16);

    // Create color stops with different opacity
    radialGradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.7)`);
    radialGradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

    // Create directional gradient for side lighting
    const directionalGradient = ctx.createLinearGradient(gradientStartX, gradientStartY, gradientEndX, gradientEndY);

    directionalGradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.8)`);
    directionalGradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

    // Save current context state
    ctx.save();

    // Apply radial gradient first
    ctx.globalCompositeOperation = 'screen';
    ctx.fillStyle = radialGradient;
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);

    // Apply directional gradient
    ctx.fillStyle = directionalGradient;
    ctx.globalCompositeOperation = 'overlay';
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);

    // Restore context state
    ctx.restore();
  }

  // Render puppet/VTuber-lite effect
  private renderPuppet(ctx: CanvasRenderingContext2D, keypoints: readonly faceLandmarksDetection.Keypoint[]): void {
    // Get important face points for puppet animation
    const faceTop = keypoints[10]; // Top of forehead
    const faceBottom = keypoints[152]; // Bottom of chin
    const leftEye = keypoints[159]; // Left eye
    const rightEye = keypoints[386]; // Right eye
    const leftEyebrowTop = keypoints[66]; // Left eyebrow
    const rightEyebrowTop = keypoints[296]; // Right eyebrow
    const noseTop = keypoints[168]; // Top of nose
    const noseTip = keypoints[1]; // Tip of nose
    const mouthLeft = keypoints[61]; // Left corner of mouth
    const mouthRight = keypoints[291]; // Right corner of mouth
    const upperLip = keypoints[13]; // Upper lip
    const lowerLip = keypoints[14]; // Lower lip
    const leftCheek = keypoints[234]; // Left cheek
    const rightCheek = keypoints[454]; // Right cheek

    if (!faceTop || !faceBottom || !leftEye || !rightEye || !mouthLeft || !mouthRight) {
      return;
    }

    // Calculate face dimensions
    const faceWidth = Math.hypot(rightCheek.x - leftCheek.x, rightCheek.y - leftCheek.y);
    const faceHeight = Math.hypot(faceBottom.y - faceTop.y, faceBottom.x - faceTop.x);
    const faceScale = Math.min(faceWidth, faceHeight) / 200; // Scaling factor

    // Calculate face center
    const faceCenter = {
      x: (leftCheek.x + rightCheek.x) / 2,
      y: (faceTop.y + faceBottom.y) / 2,
    };

    // Get head tilt based on eye positions
    const eyeAngle = Math.atan2(rightEye.y - leftEye.y, rightEye.x - leftEye.x);

    // Calculate mouth openness
    const mouthOpenness = Math.hypot(upperLip.y - lowerLip.y, upperLip.x - lowerLip.x) / faceHeight;

    // Calculate eye openness - approximate using distance
    const leftEyeTop = keypoints[159]; // Top of left eye
    const leftEyeBottom = keypoints[145]; // Bottom of left eye
    const rightEyeTop = keypoints[386]; // Top of right eye
    const rightEyeBottom = keypoints[374]; // Bottom of right eye

    const leftEyeOpenness = Math.hypot(leftEyeTop.y - leftEyeBottom.y, leftEyeTop.x - leftEyeBottom.x) / faceHeight;
    const rightEyeOpenness =
      Math.hypot(rightEyeTop.y - rightEyeBottom.y, rightEyeTop.x - rightEyeBottom.x) / faceHeight;

    // Get eyebrow positions relative to eyes
    const leftEyebrowHeight = (leftEye.y - leftEyebrowTop.y) / faceHeight;
    const rightEyebrowHeight = (rightEye.y - rightEyebrowTop.y) / faceHeight;

    // Get nose direction for head rotation
    const noseVector = {
      x: noseTip.x - noseTop.x,
      y: noseTip.y - noseTop.y,
    };

    // Clear the canvas first with a transparent rectangle
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

    // Set transformation for the whole puppet
    ctx.save();
    ctx.translate(faceCenter.x, faceCenter.y);
    ctx.rotate(eyeAngle); // Rotate based on head tilt
    ctx.scale(faceScale * this.facialEffectSize, faceScale * this.facialEffectSize); // Scale based on face size and user's size setting

    // Parse the face color
    const r = parseInt(this.facialEffectColor.slice(1, 3), 16);
    const g = parseInt(this.facialEffectColor.slice(3, 5), 16);
    const b = parseInt(this.facialEffectColor.slice(5, 7), 16);

    // Draw face shape (a simple cartoon character)
    ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
    ctx.strokeStyle = `rgb(${Math.max(0, r - 50)}, ${Math.max(0, g - 50)}, ${Math.max(0, b - 50)})`;
    ctx.lineWidth = 5;

    // Face shape
    ctx.beginPath();
    ctx.ellipse(0, 0, 100, 120, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Eyes
    const eyeX = 40;
    const eyeY = -30;
    const eyeSize = 20;

    // Draw eye whites
    ctx.fillStyle = 'white';
    ctx.beginPath();

    // Scale eyes based on openness
    const leftEyeScale = Math.max(0.1, leftEyeOpenness * 10);
    const rightEyeScale = Math.max(0.1, rightEyeOpenness * 10);

    // Left eye
    ctx.save();
    ctx.translate(-eyeX, eyeY);
    ctx.scale(1, leftEyeScale);
    ctx.ellipse(0, 0, eyeSize, eyeSize, 0, 0, Math.PI * 2);
    ctx.restore();

    // Right eye
    ctx.save();
    ctx.translate(eyeX, eyeY);
    ctx.scale(1, rightEyeScale);
    ctx.ellipse(0, 0, eyeSize, eyeSize, 0, 0, Math.PI * 2);
    ctx.restore();

    ctx.fill();
    ctx.stroke();

    // Pupils - follow the nose direction for a gaze effect
    ctx.fillStyle = 'black';

    // Calculate pupil offset based on nose direction
    const pupilOffsetX = noseVector.x * 10;
    const pupilOffsetY = noseVector.y * 10;

    // Left pupil
    ctx.beginPath();
    ctx.arc(-eyeX + pupilOffsetX, eyeY + pupilOffsetY, eyeSize * 0.5, 0, Math.PI * 2);
    ctx.fill();

    // Right pupil
    ctx.beginPath();
    ctx.arc(eyeX + pupilOffsetX, eyeY + pupilOffsetY, eyeSize * 0.5, 0, Math.PI * 2);
    ctx.fill();

    // Eyebrows - react to eyebrow position
    ctx.strokeStyle = 'black';
    ctx.lineWidth = 8;

    // Left eyebrow
    ctx.beginPath();
    const leftEyebrowY = eyeY - 20 - leftEyebrowHeight * 50;
    ctx.moveTo(-eyeX - 20, leftEyebrowY);
    ctx.lineTo(-eyeX + 20, leftEyebrowY);
    ctx.stroke();

    // Right eyebrow
    ctx.beginPath();
    const rightEyebrowY = eyeY - 20 - rightEyebrowHeight * 50;
    ctx.moveTo(eyeX - 20, rightEyebrowY);
    ctx.lineTo(eyeX + 20, rightEyebrowY);
    ctx.stroke();

    // Mouth - adjust based on mouth openness
    const mouthY = 40;
    const mouthWidth = 70;
    const mouthOpenScale = Math.max(0.5, mouthOpenness * 20);
    const mouthHeight = 10 * mouthOpenScale;

    // Map the original color to a darker shade for the mouth
    ctx.fillStyle = `rgb(${Math.max(0, r - 80)}, ${Math.max(0, g - 80)}, ${Math.max(0, b - 80)})`;

    ctx.beginPath();
    ctx.ellipse(0, mouthY, mouthWidth / 2, mouthHeight, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Add a simple nose
    ctx.fillStyle = `rgb(${Math.max(0, r - 40)}, ${Math.max(0, g - 40)}, ${Math.max(0, b - 40)})`;
    ctx.beginPath();
    ctx.arc(0, 10, 15, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Restore the canvas context
    ctx.restore();
  }

  // Weather-themed effects: Rain Effect
  private renderRain(ctx: CanvasRenderingContext2D, keypoints: readonly faceLandmarksDetection.Keypoint[]): void {
    // Define face boundary for rain to interact with
    const faceContour = keypoints.slice(0, 36); // Face outline points
    const faceTop = keypoints[10]; // Top of forehead
    const faceBottom = keypoints[152]; // Bottom of chin
    const leftCheek = keypoints[234]; // Left cheek
    const rightCheek = keypoints[454]; // Right cheek

    if (!faceTop || !faceBottom || !leftCheek || !rightCheek) {
      return;
    }

    // Calculate face dimensions and position
    const faceWidth = Math.hypot(rightCheek.x - leftCheek.x, rightCheek.y - leftCheek.y);
    const faceHeight = Math.hypot(faceBottom.y - faceTop.y, faceBottom.x - faceTop.x);
    const faceCenterX = (leftCheek.x + rightCheek.x) / 2;
    const faceCenterY = (faceTop.y + faceBottom.y) / 2;

    // Parse the color for rain drops
    const r = parseInt(this.facialEffectColor.slice(1, 3), 16);
    const g = parseInt(this.facialEffectColor.slice(3, 5), 16);
    const b = parseInt(this.facialEffectColor.slice(5, 7), 16);

    const rainColor = `rgba(${r}, ${g}, ${b}, 0.7)`;
    const rainSplashColor = `rgba(${r}, ${g}, ${b}, 0.4)`;

    // Calculate rain drop parameters based on face size and effect size
    const dropCount = Math.floor(50 * this.facialEffectSize);
    const dropLength = this.facialEffectSize * 2 + 5;
    const dropWidth = this.facialEffectSize / 2;
    const splashRadius = this.facialEffectSize / 2 + 2;

    // Setup for consistent animation
    const time = Date.now() / 1000;

    // Draw rain drops
    ctx.strokeStyle = rainColor;
    ctx.fillStyle = rainColor;
    ctx.lineWidth = dropWidth;
    ctx.lineCap = 'round';

    for (let i = 0; i < dropCount; i++) {
      // Generate pseudorandom but consistent positions for drops
      const seed = i * 0.1;
      const xOffset = Math.sin(seed) * faceWidth * 1.5;
      const yOffset = (Math.cos(seed * 2) * faceHeight) / 2;

      // Calculate drop position and speed
      const dropX = faceCenterX + xOffset;
      const dropY = faceCenterY - faceHeight + yOffset;
      const dropSpeed = 2 + Math.sin(seed * 3) * 0.5;

      // Animate the rain drops moving down
      const animY = (time * 100 * dropSpeed) % (ctx.canvas.height * 1.2);
      const drawY = dropY + animY;

      // Check if the drop is "hitting" the face
      let isHittingFace = false;

      // Very simple collision detection with face oval
      if (drawY > faceCenterY - faceHeight / 2 && drawY < faceCenterY + faceHeight / 2) {
        const distanceToCenter = Math.sqrt(
          Math.pow((dropX - faceCenterX) / (faceWidth / 2), 2) + Math.pow((drawY - faceCenterY) / (faceHeight / 2), 2),
        );

        if (distanceToCenter < 1) {
          isHittingFace = true;
          // Draw splash effect
          ctx.beginPath();
          ctx.arc(dropX, drawY, splashRadius, 0, Math.PI * 2);
          ctx.fillStyle = rainSplashColor;
          ctx.fill();

          // Draw little splash lines
          ctx.beginPath();
          for (let j = 0; j < 3; j++) {
            const angle = (j * Math.PI) / 1.5 + seed;
            ctx.moveTo(dropX, drawY);
            ctx.lineTo(dropX + Math.cos(angle) * splashRadius * 2, drawY + Math.sin(angle) * splashRadius * 2);
          }
          ctx.strokeStyle = rainSplashColor;
          ctx.stroke();

          continue; // Skip drawing the drop
        }
      }

      // Draw the rain drop if not hitting face
      if (!isHittingFace) {
        ctx.beginPath();
        ctx.moveTo(dropX, drawY);
        ctx.lineTo(dropX, drawY + dropLength);
        ctx.strokeStyle = rainColor;
        ctx.stroke();
      }
    }
  }

  // Weather-themed effects: Snow Effect
  private renderSnow(ctx: CanvasRenderingContext2D, keypoints: readonly faceLandmarksDetection.Keypoint[]): void {
    // Define face boundary for snow to interact with
    const faceContour = keypoints.slice(0, 36); // Face outline points
    const faceTop = keypoints[10]; // Top of forehead
    const faceBottom = keypoints[152]; // Bottom of chin
    const leftCheek = keypoints[234]; // Left cheek
    const rightCheek = keypoints[454]; // Right cheek

    if (!faceTop || !faceBottom || !leftCheek || !rightCheek) {
      return;
    }

    // Calculate face dimensions and position
    const faceWidth = Math.hypot(rightCheek.x - leftCheek.x, rightCheek.y - leftCheek.y);
    const faceHeight = Math.hypot(faceBottom.y - faceTop.y, faceBottom.x - faceTop.x);
    const faceCenterX = (leftCheek.x + rightCheek.x) / 2;
    const faceCenterY = (faceTop.y + faceBottom.y) / 2;

    // Parse the color for snowflakes
    const r = parseInt(this.facialEffectColor.slice(1, 3), 16);
    const g = parseInt(this.facialEffectColor.slice(3, 5), 16);
    const b = parseInt(this.facialEffectColor.slice(5, 7), 16);

    // Calculate snowflake parameters based on face size and effect size
    const snowflakeCount = Math.floor(40 * this.facialEffectSize);
    const maxSize = this.facialEffectSize * 1.5 + 2;

    // Setup for consistent animation
    const time = Date.now() / 1000;

    // Draw snowflakes
    for (let i = 0; i < snowflakeCount; i++) {
      // Generate pseudorandom but consistent positions for snowflakes
      const seed = i * 0.1;
      const xOffset = Math.sin(seed * 5) * faceWidth * 2;
      const yOffset = Math.cos(seed * 3) * faceHeight * 2;

      // Calculate snowflake position and variation
      const flakeX = faceCenterX + xOffset;
      const flakeY = faceCenterY - faceHeight * 1.5 + yOffset;
      const flakeSize = (Math.sin(seed * 8) * 0.5 + 0.5) * maxSize + 1;

      // Side-to-side motion
      const wobbleX = Math.sin(time * (0.5 + seed * 0.2)) * 10;

      // Animate the snowflakes falling down
      const fallSpeed = 0.2 + Math.sin(seed * 7) * 0.1;
      const animY = (time * 50 * fallSpeed) % (ctx.canvas.height * 1.2);
      const drawX = flakeX + wobbleX;
      const drawY = flakeY + animY;

      // Calculate opacity based on position (fade in/out at edges)
      const distFromCenter = Math.abs((drawX - faceCenterX) / faceWidth);
      const opacity = Math.max(0, 1 - distFromCenter * 0.5);

      // Check if the snowflake is "hitting" the face
      let isHittingFace = false;

      // Very simple collision detection with face oval
      if (drawY > faceCenterY - faceHeight / 2 && drawY < faceCenterY + faceHeight / 2) {
        const distanceToCenter = Math.sqrt(
          Math.pow((drawX - faceCenterX) / (faceWidth / 2), 2) + Math.pow((drawY - faceCenterY) / (faceHeight / 2), 2),
        );

        if (distanceToCenter < 1) {
          isHittingFace = true;
          // Make snowflakes on face slightly transparent
          ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${opacity * 0.4})`;
        } else {
          ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${opacity})`;
        }
      } else {
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${opacity})`;
      }

      // Draw the snowflake
      this.drawSnowflake(ctx, drawX, drawY, flakeSize, seed);
    }
  }

  // Helper method to draw a snowflake
  private drawSnowflake(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, seed: number): void {
    const rotation = seed * 30; // Rotation in degrees
    const branches = (Math.floor(seed * 10) % 3) + 5; // 5-7 branches

    ctx.save();
    ctx.translate(x, y);
    ctx.rotate((rotation * Math.PI) / 180);

    // Draw main branches
    for (let i = 0; i < branches; i++) {
      const angle = (i / branches) * Math.PI * 2;
      ctx.save();
      ctx.rotate(angle);

      // Main branch
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(0, size);
      ctx.stroke();

      // Sub-branches
      const subBranchCount = (Math.floor(seed * 10) % 2) + 2; // 2-3 sub-branches
      for (let j = 1; j <= subBranchCount; j++) {
        const subLength = size * (0.3 + (j / subBranchCount) * 0.4);
        const subY = (size * j) / (subBranchCount + 1);

        ctx.beginPath();
        ctx.moveTo(0, subY);
        ctx.lineTo(subLength, subY + subLength * 0.3);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(0, subY);
        ctx.lineTo(-subLength, subY + subLength * 0.3);
        ctx.stroke();
      }

      ctx.restore();
    }

    // Draw center dot
    ctx.beginPath();
    ctx.arc(0, 0, size * 0.1, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  // Weather-themed effects: Bubbles Effect
  private renderBubbles(ctx: CanvasRenderingContext2D, keypoints: readonly faceLandmarksDetection.Keypoint[]): void {
    // Define face boundary for bubbles to interact with
    const faceTop = keypoints[10]; // Top of forehead
    const faceBottom = keypoints[152]; // Bottom of chin
    const leftCheek = keypoints[234]; // Left cheek
    const rightCheek = keypoints[454]; // Right cheek
    const mouthCenter = keypoints[13]; // Center of upper lip

    if (!faceTop || !faceBottom || !leftCheek || !rightCheek || !mouthCenter) {
      return;
    }

    // Calculate face dimensions and position
    const faceWidth = Math.hypot(rightCheek.x - leftCheek.x, rightCheek.y - leftCheek.y);
    const faceHeight = Math.hypot(faceBottom.y - faceTop.y, faceBottom.x - faceTop.x);
    const faceCenterX = (leftCheek.x + rightCheek.x) / 2;
    const faceCenterY = (faceTop.y + faceBottom.y) / 2;

    // Parse the color for bubbles
    const r = parseInt(this.facialEffectColor.slice(1, 3), 16);
    const g = parseInt(this.facialEffectColor.slice(3, 5), 16);
    const b = parseInt(this.facialEffectColor.slice(5, 7), 16);

    // Calculate bubble parameters
    const bubbleCount = Math.floor(20 * this.facialEffectSize);
    const maxSize = this.facialEffectSize * 3 + 5;

    // Setup for consistent animation
    const time = Date.now() / 1000;

    // Bubbles come from the mouth area
    const sourceX = mouthCenter.x;
    const sourceY = mouthCenter.y;

    // Draw bubbles
    for (let i = 0; i < bubbleCount; i++) {
      // Generate pseudorandom but consistent characteristics for bubbles
      const seed = i * 0.1;
      const angle = seed * Math.PI * 2; // Direction of bubble movement
      const distance = ((time * 0.5 + seed * 5) % 1) * faceWidth * 2; // How far from source

      // Bubble size varies and grows slightly as it moves away
      const growFactor = 1 + distance / (faceWidth * 2);
      const bubbleSize = ((Math.sin(seed * 8) * 0.5 + 0.5) * maxSize + 3) * growFactor;

      // Calculate bubble position with wobble motion
      const wobbleX = Math.sin(time * (2 + seed)) * 5 * growFactor;
      const wobbleY = Math.cos(time * (2 + seed * 0.7)) * 5 * growFactor;

      // Bubbles float upward and outward from mouth
      const bubbleX = sourceX + Math.cos(angle) * distance * 0.5 + wobbleX;
      const bubbleY = sourceY - distance + wobbleY; // Negative because bubbles rise up

      // Calculate opacity - bubbles fade out as they move away
      const fadeStart = faceWidth;
      const fadeEnd = faceWidth * 2;
      let opacity = 1;

      if (distance > fadeStart) {
        opacity = 1 - Math.min(1, (distance - fadeStart) / (fadeEnd - fadeStart));
      }

      // Skip if completely faded
      if (opacity <= 0) {
        continue;
      }

      // Draw the bubble
      this.drawBubble(ctx, bubbleX, bubbleY, bubbleSize, r, g, b, opacity);
    }
  }

  // Helper method to draw a bubble
  private drawBubble(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    size: number,
    r: number,
    g: number,
    b: number,
    opacity: number,
  ): void {
    // Bubble outline
    ctx.beginPath();
    ctx.arc(x, y, size, 0, Math.PI * 2);
    ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${opacity})`;
    ctx.lineWidth = 1;
    ctx.stroke();

    // Bubble fill - very transparent
    ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${opacity * 0.1})`;
    ctx.fill();

    // Bubble highlight/reflection (small white circle)
    const highlightSize = size * 0.2;
    const highlightX = x - size * 0.3;
    const highlightY = y - size * 0.3;

    ctx.beginPath();
    ctx.arc(highlightX, highlightY, highlightSize, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(255, 255, 255, ${opacity * 0.7})`;
    ctx.fill();

    // Add a second smaller highlight
    ctx.beginPath();
    ctx.arc(x + size * 0.1, y - size * 0.1, highlightSize * 0.5, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(255, 255, 255, ${opacity * 0.5})`;
    ctx.fill();
  }

  // Weather-themed effects: Sparkles Effect
  private renderSparkles(ctx: CanvasRenderingContext2D, keypoints: readonly faceLandmarksDetection.Keypoint[]): void {
    // Define face boundary for sparkles
    const faceTop = keypoints[10]; // Top of forehead
    const faceBottom = keypoints[152]; // Bottom of chin
    const leftCheek = keypoints[234]; // Left cheek
    const rightCheek = keypoints[454]; // Right cheek

    if (!faceTop || !faceBottom || !leftCheek || !rightCheek) {
      return;
    }

    // Calculate face dimensions and position
    const faceWidth = Math.hypot(rightCheek.x - leftCheek.x, rightCheek.y - leftCheek.y);
    const faceHeight = Math.hypot(faceBottom.y - faceTop.y, faceBottom.x - faceTop.x);
    const faceCenterX = (leftCheek.x + rightCheek.x) / 2;
    const faceCenterY = (faceTop.y + faceBottom.y) / 2;

    // Parse the color for sparkles
    const r = parseInt(this.facialEffectColor.slice(1, 3), 16);
    const g = parseInt(this.facialEffectColor.slice(3, 5), 16);
    const b = parseInt(this.facialEffectColor.slice(5, 7), 16);

    // Calculate sparkle parameters
    const sparkleCount = Math.floor(30 * this.facialEffectSize);
    const maxSize = this.facialEffectSize * 1.5 + 3;

    // Setup for consistent animation
    const time = Date.now() / 1000;

    // Draw sparkles
    for (let i = 0; i < sparkleCount; i++) {
      // Generate pseudorandom but consistent positions for sparkles
      const seed = i * 0.1;
      const angle = seed * Math.PI * 2;
      const distance = faceWidth * 0.7 * (Math.sin(seed * 8) * 0.3 + 0.7);

      // Position sparkles around and on the face
      const sparkleX = faceCenterX + Math.cos(angle) * distance;
      const sparkleY = faceCenterY + Math.sin(angle) * distance * (faceHeight / faceWidth);

      // Sparkle animation - pulsing and twinkling
      const twinkleSpeed = 3 + seed * 5;
      const twinklePhase = (time * twinkleSpeed + seed * 10) % (2 * Math.PI);
      const twinkleFactor = (Math.sin(twinklePhase) * 0.5 + 0.5) * 0.8 + 0.2; // 0.2 to 1.0

      // Size varies with twinkle
      const sparkleSize = maxSize * twinkleFactor;

      // Opacity varies with twinkle
      const opacity = twinkleFactor * 0.8 + 0.2; // 0.2 to 1.0

      // Check if sparkle is on the face
      let isOnFace = false;

      // Simple collision detection with face oval
      const distanceToCenter = Math.sqrt(
        Math.pow((sparkleX - faceCenterX) / (faceWidth / 2), 2) +
          Math.pow((sparkleY - faceCenterY) / (faceHeight / 2), 2),
      );

      if (distanceToCenter < 1) {
        isOnFace = true;
      }

      // Draw the sparkle
      this.drawSparkle(ctx, sparkleX, sparkleY, sparkleSize, r, g, b, opacity, isOnFace, time + seed);
    }
  }

  // Helper method to draw a sparkle
  private drawSparkle(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    size: number,
    r: number,
    g: number,
    b: number,
    opacity: number,
    isOnFace: boolean,
    timeOffset: number,
  ): void {
    const rotationAngle = timeOffset * 2;

    // Adjust opacity if on face
    const finalOpacity = isOnFace ? opacity * 0.7 : opacity;
    const sparkleColor = `rgba(${r}, ${g}, ${b}, ${finalOpacity})`;
    const glowColor = `rgba(${Math.min(255, r + 50)}, ${Math.min(255, g + 50)}, ${Math.min(255, b + 50)}, ${finalOpacity * 0.7})`;

    // Set shadow for glow effect
    ctx.shadowColor = glowColor;
    ctx.shadowBlur = size;

    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rotationAngle);

    // Draw a 4-point star
    ctx.beginPath();

    // Main points
    ctx.moveTo(0, -size);
    ctx.lineTo(0, size);
    ctx.moveTo(-size, 0);
    ctx.lineTo(size, 0);

    // Optional diagonal lines for 8-point star
    const diagSize = size * 0.7;
    ctx.moveTo(-diagSize, -diagSize);
    ctx.lineTo(diagSize, diagSize);
    ctx.moveTo(-diagSize, diagSize);
    ctx.lineTo(diagSize, -diagSize);

    ctx.strokeStyle = sparkleColor;
    ctx.lineWidth = Math.max(1, size / 5);
    ctx.stroke();

    // Draw center dot
    ctx.beginPath();
    ctx.arc(0, 0, size / 4, 0, Math.PI * 2);
    ctx.fillStyle = glowColor;
    ctx.fill();

    ctx.restore();

    // Reset shadow
    ctx.shadowBlur = 0;
  }

  // Weather-themed effects: Floating Particles Effect
  private renderParticles(ctx: CanvasRenderingContext2D, keypoints: readonly faceLandmarksDetection.Keypoint[]): void {
    // Define face boundary for particles
    const faceTop = keypoints[10]; // Top of forehead
    const faceBottom = keypoints[152]; // Bottom of chin
    const leftCheek = keypoints[234]; // Left cheek
    const rightCheek = keypoints[454]; // Right cheek

    if (!faceTop || !faceBottom || !leftCheek || !rightCheek) {
      return;
    }

    // Calculate face dimensions and position
    const faceWidth = Math.hypot(rightCheek.x - leftCheek.x, rightCheek.y - leftCheek.y);
    const faceHeight = Math.hypot(faceBottom.y - faceTop.y, faceBottom.x - faceTop.x);
    const faceCenterX = (leftCheek.x + rightCheek.x) / 2;
    const faceCenterY = (faceTop.y + faceBottom.y) / 2;

    // Parse the color for particles
    const r = parseInt(this.facialEffectColor.slice(1, 3), 16);
    const g = parseInt(this.facialEffectColor.slice(3, 5), 16);
    const b = parseInt(this.facialEffectColor.slice(5, 7), 16);

    // Calculate particle parameters
    const particleCount = Math.floor(60 * this.facialEffectSize);
    const maxSize = this.facialEffectSize * 1.2 + 1;

    // Setup for consistent animation
    const time = Date.now() / 1000;

    // Define particle shapes - cycle through them
    const shapes = ['circle', 'square', 'triangle', 'line'];

    // Draw particles
    for (let i = 0; i < particleCount; i++) {
      // Generate pseudorandom but consistent characteristics for particles
      const seed = i * 0.1;

      // Particle positions cover a wider area around the face
      const angle = seed * Math.PI * 2;
      const distance = faceWidth * (1 + Math.sin(seed * 8) * 0.5);

      // Base position
      let particleX = faceCenterX + Math.cos(angle) * distance;
      let particleY = faceCenterY + Math.sin(angle) * distance * (faceHeight / faceWidth);

      // Add organic floating motion
      particleX += Math.sin(time * (0.5 + seed * 0.3) + seed * 10) * 15;
      particleY += Math.cos(time * (0.3 + seed * 0.2) + seed * 5) * 10;

      // Vary size
      const particleSize = (Math.sin(seed * 15) * 0.5 + 0.5) * maxSize + 1;

      // Vary opacity
      const baseOpacity = Math.sin(time * (0.2 + seed * 0.1) + seed * 20) * 0.3 + 0.7; // 0.4 to 1.0

      // Check if particle is on the face
      let isOnFace = false;

      // Simple collision detection with face oval
      const distanceToCenter = Math.sqrt(
        Math.pow((particleX - faceCenterX) / (faceWidth / 2), 2) +
          Math.pow((particleY - faceCenterY) / (faceHeight / 2), 2),
      );

      if (distanceToCenter < 1) {
        isOnFace = true;
      }

      // Adjust opacity if on face
      const opacity = isOnFace ? baseOpacity * 0.5 : baseOpacity;

      // Select particle shape - different shapes for visual interest
      const shapeIndex = Math.floor(seed * 10) % shapes.length;
      const shape = shapes[shapeIndex];

      // Rotation for shapes (not circles)
      const rotation = time * (0.2 + seed * 0.2) + seed * 5;

      // Draw the particle
      this.drawParticle(ctx, particleX, particleY, particleSize, shape, rotation, r, g, b, opacity);
    }
  }

  // Helper method to draw a particle
  private drawParticle(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    size: number,
    shape: string,
    rotation: number,
    r: number,
    g: number,
    b: number,
    opacity: number,
  ): void {
    const particleColor = `rgba(${r}, ${g}, ${b}, ${opacity})`;

    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rotation);
    ctx.fillStyle = particleColor;

    switch (shape) {
      case 'circle':
        ctx.beginPath();
        ctx.arc(0, 0, size, 0, Math.PI * 2);
        ctx.fill();
        break;

      case 'square':
        ctx.fillRect(-size, -size, size * 2, size * 2);
        break;

      case 'triangle':
        ctx.beginPath();
        ctx.moveTo(0, -size);
        ctx.lineTo(-size, size);
        ctx.lineTo(size, size);
        ctx.closePath();
        ctx.fill();
        break;

      case 'line':
        ctx.beginPath();
        ctx.moveTo(-size, 0);
        ctx.lineTo(size, 0);
        ctx.lineWidth = Math.max(1, size / 2);
        ctx.strokeStyle = particleColor;
        ctx.stroke();
        break;
    }

    ctx.restore();
  }
}
